import React from 'react';
import Input from '../../../components/ui/Input';
import ImageUploadField from '../../../components/media/ImageUploadField';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const CmsRsvpSection = ({ content, onChange, weddingId }) => {
  const rsvp = content?.rsvp || {};
  const events = rsvp.events || [];
  const mealOptions = rsvp.mealOptions || [];
  const importantInfo = rsvp.importantInfo || [];

  const updateRsvp = (field, value) => {
    onChange({
      ...content,
      rsvp: { ...rsvp, [field]: value },
    });
  };

  const updateEvent = (index, field, value) => {
    const updated = [...events];
    updated[index] = { ...updated[index], [field]: value };
    updateRsvp('events', updated);
  };

  const updateMealOption = (index, field, value) => {
    const updated = [...mealOptions];
    updated[index] = { ...updated[index], [field]: value };
    updateRsvp('mealOptions', updated);
  };

  const addMealOption = () => {
    updateRsvp('mealOptions', [...mealOptions, { value: '', label: '' }]);
  };

  const removeMealOption = (index) => {
    updateRsvp('mealOptions', mealOptions.filter((_, i) => i !== index));
  };

  const updateInfoItem = (index, value) => {
    const updated = [...importantInfo];
    updated[index] = value;
    updateRsvp('importantInfo', updated);
  };

  const addInfoItem = () => {
    updateRsvp('importantInfo', [...importantInfo, '']);
  };

  const removeInfoItem = (index) => {
    updateRsvp('importantInfo', importantInfo.filter((_, i) => i !== index));
  };

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 shadow-romantic-sm border border-border">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Icon name="Mail" size={20} color="var(--color-primary)" />
        </div>
        <h3 className="text-lg font-semibold text-foreground">RSVP Settings</h3>
      </div>

      <div className="space-y-6">
        {/* Form Style */}
        <div className="p-4 bg-background rounded-lg border border-border">
          <h4 className="text-sm font-semibold text-foreground uppercase tracking-wide mb-3">Form Style</h4>
          <div className="flex flex-col gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="formStyle"
                value="classic"
                checked={rsvp.formStyle !== 'step-by-step'}
                onChange={() => updateRsvp('formStyle', 'classic')}
                className="w-4 h-4 text-primary focus:ring-primary border-border"
              />
              <span className="text-sm text-foreground">Classic Form (All fields at once)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="formStyle"
                value="step-by-step"
                checked={rsvp.formStyle === 'step-by-step'}
                onChange={() => updateRsvp('formStyle', 'step-by-step')}
                className="w-4 h-4 text-primary focus:ring-primary border-border"
              />
              <span className="text-sm text-foreground">Step-by-Step (Mobile-optimized)</span>
            </label>
          </div>
        </div>

        {/* Events */}
        {events.map((event, idx) => (
          <div key={event.id || idx} className="p-4 bg-background rounded-lg border border-border space-y-3">
            <h4 className="text-sm font-semibold text-foreground uppercase tracking-wide">
              {event.title || `Event ${idx + 1}`}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input label="Title" type="text" value={event.title || ''} onChange={(e) => updateEvent(idx, 'title', e.target.value)} />
              <Input label="Icon" type="text" placeholder="e.g., Church" value={event.icon || ''} onChange={(e) => updateEvent(idx, 'icon', e.target.value)} />
              <Input label="Date" type="text" value={event.date || ''} onChange={(e) => updateEvent(idx, 'date', e.target.value)} />
              <Input label="Time" type="text" value={event.time || ''} onChange={(e) => updateEvent(idx, 'time', e.target.value)} />
              <Input label="Venue" type="text" value={event.venue || ''} onChange={(e) => updateEvent(idx, 'venue', e.target.value)} />
              <Input label="Address" type="text" value={event.address || ''} onChange={(e) => updateEvent(idx, 'address', e.target.value)} />
            </div>
            <ImageUploadField label="Image URL" value={event.image || ''} onChange={(e) => updateEvent(idx, 'image', e.target.value)} showPreview={true} weddingId={weddingId} />
            <Input label="Image Alt Text" type="text" value={event.imageAlt || ''} onChange={(e) => updateEvent(idx, 'imageAlt', e.target.value)} />
          </div>
        ))}

        {/* Meal Options */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-foreground uppercase tracking-wide">Meal Options</h4>
            <Button variant="outline" size="sm" onClick={addMealOption} iconName="Plus" iconPosition="left">
              Add
            </Button>
          </div>
          <div className="space-y-2">
            {mealOptions.map((option, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input
                    type="text"
                    placeholder="Value (e.g., chicken)"
                    value={option.value || ''}
                    onChange={(e) => updateMealOption(idx, 'value', e.target.value)}
                  />
                  <Input
                    type="text"
                    placeholder="Label (e.g., Herb-Roasted Chicken...)"
                    value={option.label || ''}
                    onChange={(e) => updateMealOption(idx, 'label', e.target.value)}
                  />
                </div>
                <button
                  onClick={() => removeMealOption(idx)}
                  className="p-2 text-muted-foreground hover:text-error transition-romantic"
                >
                  <Icon name="Trash2" size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Important Info */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-foreground uppercase tracking-wide">Important Info Bullets</h4>
            <Button variant="outline" size="sm" onClick={addInfoItem} iconName="Plus" iconPosition="left">
              Add
            </Button>
          </div>
          <div className="space-y-2">
            {importantInfo.map((info, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <div className="flex-1">
                  <Input
                    type="text"
                    placeholder="e.g., Please arrive 15 minutes early"
                    value={info || ''}
                    onChange={(e) => updateInfoItem(idx, e.target.value)}
                  />
                </div>
                <button
                  onClick={() => removeInfoItem(idx)}
                  className="p-2 text-muted-foreground hover:text-error transition-romantic"
                >
                  <Icon name="Trash2" size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Success Confirmation */}
        <div className="p-4 bg-background rounded-lg border border-border space-y-3">
          <h4 className="text-sm font-semibold text-foreground uppercase tracking-wide">Success Confirmation</h4>
          <ImageUploadField
            label="Success Image URL"
            value={rsvp.successImage || ''}
            onChange={(e) => updateRsvp('successImage', e.target.value)}
            showPreview={true}
            weddingId={weddingId}
          />
          <Input
            label="Save the Date Text"
            type="text"
            value={rsvp.saveTheDateText || ''}
            onChange={(e) => updateRsvp('saveTheDateText', e.target.value)}
          />
        </div>
      </div>
    </div>
  );
};

export default CmsRsvpSection;
